import type { ReploAnnouncementTag } from "schemas/generated/announcement";

import * as React from "react";

import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { useListAnnouncements } from "@v2/features/announcements/hooks/useListAnnouncements";
import { extractTextFromFirstParagraph } from "@v2/features/announcements/utils/announcement";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ChevronLeft, ChevronRight } from "lucide-react";

const AnnouncementBanner = ({ tags }: { tags: ReploAnnouncementTag[] }) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const { announcements, isLoading } = useListAnnouncements({
    tags: tags,
    limit: 3,
  });
  const currentAnnouncement = announcements[currentIndex];
  const logEvent = useLogAnalytics();

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex === 0 ? announcements.length - 1 : prevIndex - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex === announcements.length - 1 ? 0 : prevIndex + 1;
    });
  };

  const handleAnnouncementClick = () => {
    if (currentAnnouncement && currentAnnouncement.ctaLink) {
      window.open(currentAnnouncement.ctaLink, "_blank");
      logEvent("announcements.clicked", {
        announcementId: currentAnnouncement.id,
        announcementWebflowId: currentAnnouncement.webflowId,
        source: "AnnouncementBanner",
      });
    }
  };

  if (isLoading || announcements.length === 0 || !currentAnnouncement) {
    return null;
  }

  return (
    <div className="bg-neutral-soft border-0.5 border-border flex justify-between rounded-lg p-4">
      <div className="flex max-h-24 w-full gap-4">
        {currentAnnouncement.imageUrl && (
          <img
            src={currentAnnouncement.imageUrl}
            alt="Announcement"
            className="h-full w-auto rounded-sm"
            loading="lazy"
          />
        )}

        <div className="flex flex-col items-start justify-center">
          <p className="mb-1 text-sm font-medium">
            {currentAnnouncement.title}
          </p>
          <p className="mb-2 line-clamp-2 overflow-hidden text-sm">
            {extractTextFromFirstParagraph(currentAnnouncement.description)}
          </p>

          {currentAnnouncement.ctaText && currentAnnouncement.ctaLink && (
            <div
              className="text-primary cursor-pointer text-xs font-medium"
              onClick={handleAnnouncementClick}
            >
              {currentAnnouncement.ctaText}
            </div>
          )}
        </div>
      </div>

      {announcements.length && (
        <div className="ml-4 flex shrink-0 gap-1">
          <DeprecatedIconButton
            icon={<ChevronLeft size={16} />}
            onClick={goToPrevious}
            size="base"
            variant="tertiary"
            aria-label="Previous announcement"
            disabled={currentIndex === 0}
            layoutClassName=" w-8 h-8"
          />
          <DeprecatedIconButton
            icon={<ChevronRight size={16} />}
            onClick={goToNext}
            size="base"
            variant="tertiary"
            aria-label="Next announcement"
            disabled={currentIndex === announcements.length - 1}
            layoutClassName="w-8 h-8"
          />
        </div>
      )}
    </div>
  );
};

export default AnnouncementBanner;
